/**
 * Performance statistics overlay for debug mode.
 * Displays real-time metrics: render rate, WS message rate, vehicle count, connection status,
 * FPS, GeoJSON serialization time, setData frequency, rendered features, heap usage.
 */

import { useEffect, useRef, useState } from 'react';
import { useSettingsStore } from '../../stores/settingsStore';
import { useVehicleStore } from '../../stores/vehicleStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { websocketService } from '../../services/websocket';
import { vehicleLayerMetrics } from '../Map/vehicleLayerMetrics';

interface Stats {
  rendersPerSec: number;
  wsMessagesPerSec: number;
  fps: number;
  serializeMs: number;
  setDataPerSec: number;
  renderedFeatures: number;
  heapMB: string;
}

function InfoIcon({ hint }: { hint: string }) {
  const [show, setShow] = useState(false);
  return (
    <span
      className="relative ml-1 cursor-help text-gray-500 hover:text-gray-300"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <svg className="inline h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
        <path
          fillRule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clipRule="evenodd"
        />
      </svg>
      {show && (
        <span className="absolute bottom-full left-0 mb-1 z-50 max-w-[160px] rounded bg-gray-700 px-2 py-1 text-[10px] text-gray-200 shadow-lg">
          {hint}
        </span>
      )}
    </span>
  );
}

export function PerformanceStats() {
  const debugMode = useSettingsStore((state) => state.debugMode);
  const showPerformanceStats = useSettingsStore((state) => state.showPerformanceStats);
  const vehicleCount = useVehicleStore((state) => state.vehicles.size);
  const wsStatus = useConnectionStore((state) => state.status);

  const [stats, setStats] = useState<Stats>({
    rendersPerSec: 0,
    wsMessagesPerSec: 0,
    fps: 0,
    serializeMs: 0,
    setDataPerSec: 0,
    renderedFeatures: 0,
    heapMB: '—',
  });

  // Track render count
  const renderCountRef = useRef(0);
  const lastRenderCountRef = useRef(0);

  // Track WebSocket message count
  const wsMessageCountRef = useRef(0);
  const lastWsMessageCountRef = useRef(0);

  // Track FPS via requestAnimationFrame
  const fpsFrameCountRef = useRef(0);
  const lastFpsCountRef = useRef(0);

  // Track setData calls
  const lastSetDataCountRef = useRef(0);

  // Increment render count on each render
  useEffect(() => {
    renderCountRef.current += 1;
  });

  // FPS counter via requestAnimationFrame
  useEffect(() => {
    if (!debugMode || !showPerformanceStats) return;
    let rafId: number;
    const tick = () => {
      fpsFrameCountRef.current++;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [debugMode, showPerformanceStats]);

  // Calculate per-second rates
  useEffect(() => {
    if (!debugMode || !showPerformanceStats) return;

    // Initialize to current values to avoid counting pre-existing activity
    lastRenderCountRef.current = renderCountRef.current;
    lastWsMessageCountRef.current = wsMessageCountRef.current;
    lastFpsCountRef.current = fpsFrameCountRef.current;
    lastSetDataCountRef.current = vehicleLayerMetrics.setDataCount;

    const interval = setInterval(() => {
      // Calculate deltas for this second
      const renderDelta = renderCountRef.current - lastRenderCountRef.current;
      lastRenderCountRef.current = renderCountRef.current;

      const wsDelta = wsMessageCountRef.current - lastWsMessageCountRef.current;
      lastWsMessageCountRef.current = wsMessageCountRef.current;

      const fpsDelta = fpsFrameCountRef.current - lastFpsCountRef.current;
      lastFpsCountRef.current = fpsFrameCountRef.current;

      const setDataDelta = vehicleLayerMetrics.setDataCount - lastSetDataCountRef.current;
      lastSetDataCountRef.current = vehicleLayerMetrics.setDataCount;

      // Chrome-only heap measurement
      const memory = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
      const heapMB = memory ? (memory.usedJSHeapSize / 1024 / 1024).toFixed(0) : '—';

      setStats({
        rendersPerSec: renderDelta,
        wsMessagesPerSec: wsDelta,
        fps: fpsDelta,
        serializeMs: vehicleLayerMetrics.lastSerializationMs,
        setDataPerSec: setDataDelta,
        renderedFeatures: vehicleLayerMetrics.renderedFeatures,
        heapMB,
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [debugMode, showPerformanceStats]);

  // Track WebSocket messages
  useEffect(() => {
    if (!debugMode || !showPerformanceStats) return;

    const handler = () => {
      wsMessageCountRef.current += 1;
    };

    websocketService.addMessageHandler(handler);

    return () => {
      websocketService.removeMessageHandler(handler);
    };
  }, [debugMode, showPerformanceStats]);

  if (!debugMode || !showPerformanceStats) return null;

  return (
    <div className="fixed bottom-4 left-4 bg-gray-900/80 dark:bg-gray-800/90 text-white rounded-lg p-3 text-xs font-mono z-50 backdrop-blur-sm shadow-lg min-w-[180px]">
      <div className="font-semibold mb-2 text-yellow-400 text-[10px] uppercase tracking-wide">
        Performance
      </div>

      {/* Network & State */}
      <div className="space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            Renders/s
            <InfoIcon hint="React re-renders of this panel per second" />
          </span>
          <span className="font-medium">{stats.rendersPerSec}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            Vehicles
            <InfoIcon hint="Vehicles currently tracked" />
          </span>
          <span className="font-medium">{vehicleCount}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            WS/s
            <InfoIcon hint="WebSocket messages received per second" />
          </span>
          <span className="font-medium">{stats.wsMessagesPerSec}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            WS
            <InfoIcon hint="WebSocket connection status" />
          </span>
          <span
            className={`font-medium ${
              wsStatus === 'connected'
                ? 'text-green-400'
                : wsStatus === 'connecting' || wsStatus === 'reconnecting'
                  ? 'text-yellow-400'
                  : 'text-red-400'
            }`}
          >
            {wsStatus}
          </span>
        </div>
      </div>

      {/* Rendering metrics */}
      <div className="mt-2 pt-2 border-t border-gray-700 space-y-1">
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            FPS
            <InfoIcon hint="Browser frames per second" />
          </span>
          <span className={`font-medium ${stats.fps < 30 ? 'text-red-400' : stats.fps < 50 ? 'text-yellow-400' : 'text-green-400'}`}>
            {stats.fps}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            Serialize
            <InfoIcon hint="GeoJSON build time (ms)" />
          </span>
          <span className={`font-medium ${stats.serializeMs > 16 ? 'text-red-400' : stats.serializeMs > 8 ? 'text-yellow-400' : ''}`}>
            {stats.serializeMs.toFixed(1)}ms
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            setData/s
            <InfoIcon hint="MapLibre source updates per second" />
          </span>
          <span className="font-medium">{stats.setDataPerSec}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            Features
            <InfoIcon hint="GeoJSON features rendered" />
          </span>
          <span className="font-medium">{stats.renderedFeatures}</span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-gray-400">
            Heap
            <InfoIcon hint="JS heap usage in MB (Chrome only)" />
          </span>
          <span className="font-medium">{stats.heapMB === '—' ? '—' : `${stats.heapMB}MB`}</span>
        </div>
      </div>
    </div>
  );
}
